import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AuthPageWrapper,
  FormFieldRequirementIndicator,
  PageSpinner,
  PasswordField,
  TextField,
} from '@app/components';
import { authMessages, sharedMessages } from '@app/translations';
import { emailValidationRegex } from '@app/constants';
import { getFieldRuleValidationMessage } from '@app/utils/getFieldRuleValidationMessage';

import { useCreateAccountMutation } from '../createAccountMutations';
import { AccountCreationSuccess } from '../AccountCreationSuccess';

import './CreateAccountForm.scss';

const cnBem = createBemBlockBuilder(['createAccountForm']);

const passwordRequirements = [
  {
    title: 'One number',
    id: 'createAccount.oneNumber',
  },
  {
    title: 'Six character minimum',
    id: 'createAccount.sixCharacter',
  },
  {
    title: 'One special character',
    id: 'createAccount.oneSpecialCharacter',
  },
  {
    title: 'Do not include username',
    id: 'createAccount.notUsername',
  },
  {
    title: 'Do not include email address',
    id: 'createAccount.notEmailAddress',
  },
  {
    title: 'Do not include common phrases',
    id: 'createAccount.notCommonPhrases',
  },
];

export const CreateAccountForm = () => {
  const { $t } = useIntl();
  const form = useForm({ mode: 'onTouched' });
  const {
    handleSubmit: hookFormHandleSubmit,
    control,
    formState: { isSubmitting },
  } = form;
  const {
    isLoading,
    mutate: createAccount,
    isSuccess,
  } = useCreateAccountMutation();

  const handleSubmit = (values: FormData) => {
    createAccount(values);
  };

  if (isSuccess) {
    return (
      <AuthPageWrapper.Form>
        <AccountCreationSuccess />
      </AuthPageWrapper.Form>
    );
  }

  return (
    <AuthPageWrapper.Form
      data-auto="create-account-form"
      onSubmit={hookFormHandleSubmit(handleSubmit)}
    >
      {(isLoading || isSuccess) && (
        <PageSpinner className={cnBem('__spinner')} />
      )}
      <AuthPageWrapper.FormTitle titleLevel={1}>
        {$t({
          id: 'createAccount.formTitle',
          defaultMessage: 'Create an account',
        })}
      </AuthPageWrapper.FormTitle>
      <AuthPageWrapper.RowMessage>
        {$t({
          id: 'createAccount.formMessage',
          defaultMessage:
            'Creating an account allows you to save items for later, view your search history and more! To request items you need to register for a reader identification card after you create an account.',
        })}
      </AuthPageWrapper.RowMessage>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.firstName)}
          name="firstName"
          autoComplete="given-name"
          control={control}
          rules={{
            required: getFieldRuleValidationMessage({
              $t,
              message: sharedMessages.requiredField,
              fieldName: authMessages.firstName,
            }),
          }}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.middleName)}
          name="middleName"
          autoComplete="additional-name"
          control={control}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.lastName)}
          name="lastName"
          autoComplete="family-name"
          control={control}
          rules={{
            required: getFieldRuleValidationMessage({
              $t,
              message: sharedMessages.requiredField,
              fieldName: authMessages.lastName,
            }),
          }}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.preferredFirstName)}
          name="preferredFirstName"
          control={control}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.emailAddress)}
          name="email"
          type="email"
          autoComplete="email"
          control={control}
          rules={{
            required: getFieldRuleValidationMessage({
              $t,
              message: sharedMessages.requiredField,
              fieldName: authMessages.emailAddress,
            }),
            pattern: {
              value: emailValidationRegex,
              message: $t(authMessages.emailValidationMessage),
            },
          }}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <TextField
          label={$t(authMessages.username)}
          name="username"
          autoComplete="username"
          control={control}
          rules={{
            required: getFieldRuleValidationMessage({
              $t,
              message: sharedMessages.requiredField,
              fieldName: authMessages.username,
            }),
          }}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <PasswordField
          label={$t({
            id: 'createAccount.createPassword',
            defaultMessage: 'Create password',
          })}
          name="password"
          control={control}
          data-auto="create-password"
          autoComplete="new-password"
        />
      </AuthPageWrapper.Row>
      <div className={cnBem('__passwordRules')}>
        {passwordRequirements.map(passwordRequirement => (
          <FormFieldRequirementIndicator
            key={passwordRequirement.id}
            message={$t({
              id: passwordRequirement.id,
              defaultMessage: passwordRequirement.title,
            })}
            isValid={false}
          />
        ))}
      </div>
      <AuthPageWrapper.Row>
        <AuthPageWrapper.SubmitButton isDisabled={isSubmitting || isLoading}>
          {$t(authMessages.createAccount)}
        </AuthPageWrapper.SubmitButton>
      </AuthPageWrapper.Row>
    </AuthPageWrapper.Form>
  );
};
